import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Truck, Trash2 } from 'lucide-react';
import { apiClient } from '../utils/api.js';
import { sendContactEmail, isEmailNotificationsEnabled } from '../utils/email.js';
import SEO from '../components/SEO.js';
import Header from '../components/Header.js';
import Footer from '../components/Footer.js';
import '../styles/pages/Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    address: '',
    preferredDate: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const emailNotificationsEnabled = useMemo(() => isEmailNotificationsEnabled, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await apiClient.post('/api/contact', formData);

      if (emailNotificationsEnabled) {
        try {
          await sendContactEmail({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            service: formData.service,
            message: formData.message,
            preferredDate: formData.preferredDate || 'Not provided',
            address: formData.address || 'Not provided'
          });
          setSubmitStatus('success');
        } catch (emailError) {
          console.warn('EmailJS send failed:', emailError);
          setSubmitStatus('partial');
        }
      } else {
        setSubmitStatus('success');
      }

      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: '',
        address: '',
        preferredDate: ''
      });
    } catch (error) {
      setSubmitStatus('error');
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact Us - Get Free Quote for Moving & Junk Removal | SAP Movers"
        description="Contact SAP Movers for free quotes on moving services and junk removal. Professional, licensed, and insured. Call or fill out our contact form today."
        keywords="contact moving company, get moving quote, junk removal quote, professional movers contact"
        canonical="https://sapmovers.com/contact"
        ogTitle="Contact Us - Get Free Quote | SAP Movers"
        ogDescription="Contact us for free quotes on moving and junk removal services. Professional, licensed, and insured."
      />

      <Header />

      {/* Hero Section */}
      <section className="hero">
        <h1>Contact Us for Your Free Quote</h1>
        <p>Ready to get started? Contact us today for a free estimate on moving or junk removal services.</p>
      </section>

      {/* Contact Info & Form */}
      <section className="contact-page">
        <div className="contact-container">
          {/* Contact Information */}
          <div className="contact-info-section">
            <h2>Get In Touch</h2>
            <div className="contact-methods">
              <div className="contact-method">
                <Phone size={24} color="var(--icon-blue)" />
                <div>
                  <h3>Call Us</h3>
                  <p>(805) 940-3979</p>
                  <span>Available 7 days a week</span>
                </div>
              </div>
              
              <div className="contact-method">
                <Mail size={24} color="var(--icon-blue)" />
                <div>
                  <h3>Email Us</h3>
                  <p>info@sapmovers.com</p>
                  <span>We'll respond within 24 hours</span>
                </div>
              </div>
              
              <div className="contact-method">
                <MapPin size={24} color="var(--icon-blue)" />
                <div>
                  <h3>Service Area</h3>
                  <p>Serving the Orange County and surrounding areas</p>
                  <span>Local and long-distance moves</span>
                </div>
              </div>
            </div>

            {/* Service Quick Links */}
            <div className="service-quick-links">
              <h3>Our Services</h3>
              <div className="quick-link-cards">
                <Link to="/moving" className="quick-link-card">
                  <Truck size={32} color="var(--icon-blue)" />
                  <h4>Moving Services</h4>
                  <p>Residential, commercial, and long-distance moves</p>
                </Link>
                
                <Link to="/junk-removal" className="quick-link-card">
                  <Trash2 size={32} color="var(--icon-blue)" />
                  <h4>Junk Removal</h4>
                  <p>Furniture, appliances, and debris removal</p>
                </Link>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="contact-form-section">
            <h2>Request Your Free Quote</h2>
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="stack-on-mobile">
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="stack-on-mobile">
                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="service">Service Needed *</label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select a service</option>
                    <option value="moving">Moving Services</option>
                    <option value="junk-removal">Junk Removal</option>
                    <option value="both">Both Services</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="address">Address</label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Current address or pickup location"
                />
              </div>

              <div className="form-group">
                <label htmlFor="preferredDate">Preferred Date</label>
                <input
                  type="date"
                  id="preferredDate"
                  name="preferredDate"
                  value={formData.preferredDate}
                  onChange={handleChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="message">Additional Details *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Tell us about your needs - size of move, items to remove, special requirements, etc."
                  required
                />
              </div>
              
              {submitStatus === 'success' && (
                <div className="form-status success">
                  ✓ Thank you! We'll contact you soon with your free quote.
                </div>
              )}
              
              {submitStatus === 'partial' && (
                <div className="form-status warning">
                  ✓ Request saved, but email notification failed. We'll follow up manually.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="form-status error">
                  ✗ Sorry, there was an error. Please try again or call us directly.
                </div>
              )}
              
              <button type="submit" className="submit-button" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Get Free Quote'}
              </button>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Contact;
